/**
 * TopicalCrossLinks — Feb 12, 2026
 *
 * SEO audit finding #9, #13: FAQ, Upay and blog pages needed stronger
 * contextual internal linking to fix "Discovered - currently not indexed"
 * status and to distribute AEO/GEO impressions beyond top FAQ pages.
 *
 * Renders a compact grid of 3-6 hand-curated related links keyed by topic
 * slug (e.g. `mangal-dosha`, `shani-sade-sati`, `gun-milan`, `vastu`). Each
 * link points to a Related blog, FAQ or service page that reinforces the
 * topical cluster.
 *
 * Usage:
 *   <TopicalCrossLinks topic="mangal-dosha" lang={lang} />
 *   <TopicalCrossLinks topic="shani" />        // fallback: general links
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';

const CROSS_LINK_MAP = {
  'mangal-dosha': [
    { to: '/blog/mangal-dosha-manglik-facts-myths-and-effective-remedies', label: 'Manglik Dosha: Facts, Myths & Remedies', kind: 'Blog' },
    { to: '/upay/mangal',    label: 'मंगल ग्रह के शास्त्रीय उपाय', kind: 'Upay' },
    { to: '/gun-milan',      label: 'Gun Milan / कुंडली मिलान',    kind: 'Tool' },
    { to: '/faq/mangal-dosha-kya-hota-hai', label: 'मंगल दोष क्या होता है?', kind: 'FAQ' },
    { to: '/faq/mangal-dosha-ke-upay',       label: 'मंगल दोष के उपाय',     kind: 'FAQ' },
  ],
  'shani': [
    { to: '/upay/shani',                         label: 'शनि ग्रह के उपाय',          kind: 'Upay' },
    { to: '/blog/2026-11-saturn-rise-2026-predictions-9c496a95', label: 'Saturn Rise 2026 Predictions', kind: 'Blog' },
    { to: '/kundli',                             label: 'Free Kundli · Dasha Check',  kind: 'Tool' },
  ],
  'kundli': [
    { to: '/kundli',                          label: 'Free Kundli बनाएँ',            kind: 'Tool' },
    { to: '/gun-milan',                       label: 'Gun Milan',                     kind: 'Tool' },
    { to: '/faq/janam-kundli-kaise-banti-hai', label: 'जन्म कुंडली कैसे बनती है?',    kind: 'FAQ' },
    { to: '/faq/kundli-ke-12-bhav-kya-hain',  label: 'कुंडली के 12 भाव',              kind: 'FAQ' },
    { to: '/experts/vivek-virat-mudgal',      label: 'Acharya Vivek Mudgal',           kind: 'Consult' },
  ],
  'vastu': [
    { to: '/local/vastu-consultant-faridabad', label: 'Vastu Consultant Faridabad',   kind: 'Service' },
    { to: '/blog/vastu-tips-home',              label: 'Home Vastu Tips',              kind: 'Blog' },
    { to: '/blog/10-vastu-tips-for-a-prosperous-home-in-2026', label: '10 Vastu Tips for Prosperity', kind: 'Blog' },
    { to: '/faq/kitchen-vastu-rules',           label: 'Kitchen Vastu Rules',          kind: 'FAQ' },
    { to: '/faq/vastu-kya-hai-aur-kyu-zaruri',  label: 'वास्तु क्या है?',              kind: 'FAQ' },
  ],
  'gun-milan': [
    { to: '/gun-milan',                        label: 'Free Gun Milan · 36 Guna',     kind: 'Tool' },
    { to: '/faq/gun-milan-kya-hota-hai',       label: 'गुण मिलान क्या होता है?',     kind: 'FAQ' },
    { to: '/faq/nadi-dosha-kya-hai',           label: 'नाड़ी दोष',                    kind: 'FAQ' },
    { to: '/blog/kundli-milan-importance',     label: 'कुंडली मिलान का महत्व',       kind: 'Blog' },
  ],
  'gemstone': [
    { to: '/faq/kaun-sa-ratna-pehne',   label: 'कौन सा रत्न पहनें?', kind: 'FAQ' },
    { to: '/faq/neelam-kaise-pehne',    label: 'नीलम कैसे पहनें?',   kind: 'FAQ' },
    { to: '/blog/complete-guide-to-astrological-gemstones-which-stone-is-right-for-you', label: 'Complete Gemstone Guide', kind: 'Blog' },
  ],
  'numerology': [
    { to: '/numerology',                    label: 'Free Numerology Calculator', kind: 'Tool' },
    { to: '/faq/mool-ank-bhagyank-kya-hai', label: 'मूलांक और भाग्यांक',        kind: 'FAQ' },
  ],
  // Fallback — used when no topic matches
  '_default': [
    { to: '/kundli',           label: 'Free Kundli',            kind: 'Tool' },
    { to: '/daily-rashifal',   label: 'Daily Rashifal',         kind: 'Tool' },
    { to: '/gun-milan',        label: 'Gun Milan',              kind: 'Tool' },
    { to: '/poocho-guruji',    label: 'पूछो Guruji · ₹99',     kind: 'Service' },
    { to: '/faq',              label: 'All FAQs',               kind: 'FAQ' },
    { to: '/blog',             label: 'Blogs',                  kind: 'Content' },
  ],
};

// Derive a topic slug from any input (category, tag, blog slug)
export function deriveTopic(hint) {
  if (!hint) return '_default';
  const s = String(hint).toLowerCase();
  if (/manglik|mangal[-_ ]?dosha|mangal-dosh/i.test(s)) return 'mangal-dosha';
  if (/shani|saturn|sade[-_ ]?sati/i.test(s)) return 'shani';
  if (/vastu|vaastu/i.test(s)) return 'vastu';
  if (/gun[-_ ]?milan|matching/i.test(s)) return 'gun-milan';
  if (/gemstone|ratna|neelam|manik|moti|pukhraj/i.test(s)) return 'gemstone';
  if (/numerology|numerolog|ank|mool[-_ ]?ank/i.test(s)) return 'numerology';
  if (/kundli|kundali|birth[-_ ]?chart|janam/i.test(s)) return 'kundli';
  return '_default';
}

export default function TopicalCrossLinks({ topic, hint, lang = 'hi', title }) {
  const resolvedTopic = topic || deriveTopic(hint);
  const links = CROSS_LINK_MAP[resolvedTopic] || CROSS_LINK_MAP._default;
  const heading = title || (lang === 'hi' ? 'संबंधित शास्त्रीय संसाधन' : 'Related Vedic Resources');

  return (
    <section
      className="max-w-4xl mx-auto px-4 sm:px-0 mt-10 mb-4"
      data-testid={`topical-cross-links-${resolvedTopic}`}
    >
      <div className="rounded-2xl border border-gold/20 bg-gradient-to-br from-[#2c0b14]/60 via-[#3d101e]/40 to-[#1a0710]/60 backdrop-blur-md p-6 sm:p-7">
        <p className="text-[10px] uppercase tracking-[0.25em] text-gold/70 font-semibold mb-4">
          {heading}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          {links.slice(0, 6).map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="group flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-white/[0.03] border border-gold/15 hover:border-gold/40 hover:bg-white/[0.05] transition-colors"
              data-testid={`cross-link-${l.to.replace(/[^a-z0-9]/gi, '-')}`}
            >
              <span className="flex items-center gap-3 min-w-0">
                <span className="text-[9px] uppercase tracking-[0.15em] text-gold/70 font-semibold flex-shrink-0 w-14">
                  {l.kind}
                </span>
                <span className="text-sm text-text-main truncate group-hover:text-gold transition-colors">
                  {l.label}
                </span>
              </span>
              <ArrowUpRight className="w-4 h-4 text-gold/50 group-hover:text-gold flex-shrink-0" strokeWidth={2} />
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
